import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useAdminOrgStore } from "@/stores/admin-org";
import { API_URL, fetchJson, fetchList } from "@/lib/admin-api";
import { WASTE_TYPES, WASTE_TYPE_LABELS } from "@/lib/waste-types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { PaginationBar } from "@/components/pagination-bar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const DISAGREEMENTS_PAGE_SIZE = 25;

interface ScorerComparisonTotals {
  pairs: number;
  agreement_pct: number;
  v1_unassigned_pct: number;
  v2_unassigned_pct: number;
  unassigned_delta_pct: number;
  consensus_rows: number;
  scored_rows: number;
  with_state_evidence: number;
  evidence_coverage_pct: number;
}

interface ConfirmedCut {
  pairs: number;
  agreement_pct: number;
}

interface CrosstabCell {
  v1: string;
  v2: string;
  n: number;
}

interface SiteComparison {
  site_id: string;
  site_name: string;
  pairs: number;
  agreement_pct: number;
  v1_unassigned_pct: number;
  v2_unassigned_pct: number;
}

interface DistributionEntry {
  key: string;
  n: number;
}

interface ScorerComparison {
  totals: ScorerComparisonTotals;
  confirmed: ConfirmedCut;
  crosstab: CrosstabCell[];
  per_site: SiteComparison[];
  confidence_distribution: DistributionEntry[];
  reason_distribution: DistributionEntry[];
}

interface DisagreementEvent {
  event_id: string;
  org_id: string;
  org_name: string;
  site_id: string;
  site_name: string;
  logged_at: string;
  tier: number;
  is_confirmed: boolean;
  v1: string;
  v2: string;
  scorer_confidence: string | null;
  scorer_signals: string[] | null;
  scorer_reason: string | null;
}

interface BackfillRunCounts {
  scanned?: number;
  assigned_by_type?: Record<string, number>;
  refined_by_type?: Record<string, number>;
  skipped?: number;
  unassigned_before?: number;
  unassigned_after?: number;
}

interface BackfillRun {
  id: string;
  mode: string;
  tier: string;
  dry_run: boolean;
  scorer_live: string;
  status: string;
  counts: BackfillRunCounts;
  started_at: string;
  finished_at: string | null;
}

interface BackfillTriggerResponse {
  scheduled: boolean;
  mode: string;
  tier: number;
  dry_run: boolean;
  scorer_live: string;
  detail: string;
}

const pct = (x: number) => `${(x * 100).toFixed(1)}%`;

const sumCounts = (m?: Record<string, number>) =>
  m ? Object.values(m).reduce((a, b) => a + b, 0) : 0;

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
        {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );
}

export default function AdminMonitoringPage() {
  const headers = useApiHeaders();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setOrg = useAdminOrgStore((s) => s.setOrg);

  const [cell, setCell] = useState<{ v1: string; v2: string } | null>(null);
  const [siteFilter, setSiteFilter] = useState("all");
  const [confirmedFilter, setConfirmedFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DISAGREEMENTS_PAGE_SIZE);

  const authed = !!headers.Authorization.replace("Bearer ", "");

  const comparisonQuery = useQuery<ScorerComparison, Error>({
    queryKey: ["admin", "monitoring", "scorer-comparison"],
    queryFn: () =>
      fetchJson<ScorerComparison>(`${API_URL}/api/v1/admin/monitoring/scorer-comparison`, headers),
    enabled: authed,
    retry: false,
  });

  const disagreementsParams = new URLSearchParams();
  if (siteFilter !== "all") disagreementsParams.set("site_id", siteFilter);
  if (cell) {
    disagreementsParams.set("v1", cell.v1);
    disagreementsParams.set("v2", cell.v2);
  }
  if (confirmedFilter !== "all") disagreementsParams.set("confirmed", confirmedFilter);
  disagreementsParams.set("page", String(page + 1));
  disagreementsParams.set("page_size", String(pageSize));

  const disagreementsQuery = useQuery<{ events: DisagreementEvent[]; total: number }, Error>({
    queryKey: ["admin", "monitoring", "disagreements", disagreementsParams.toString()],
    queryFn: async () => {
      const body = await fetchList<DisagreementEvent>(
        `${API_URL}/api/v1/admin/monitoring/scorer-comparison/disagreements?${disagreementsParams}`,
        headers,
      );
      return { events: body.data, total: body.meta.total };
    },
    enabled: authed,
    retry: false,
  });

  // A shrinking result set (live classification, a relabel) can strand us past
  // the last page — the RPC then returns no rows and total 0. Step back to the
  // first page so the drill-down never dead-ends on an empty "No disagreements."
  useEffect(() => {
    const d = disagreementsQuery.data;
    if (d && d.events.length === 0 && page > 0) setPage(0);
  }, [disagreementsQuery.data, page]);

  function resetPaging() {
    setPage(0);
  }

  function selectCell(v1: string, v2: string) {
    setCell({ v1, v2 });
    resetPaging();
  }

  function openEvent(event: DisagreementEvent) {
    // Set the admin org context so the org-scoped event page fetches correctly,
    // then clear the query cache so it refetches with the new X-Org-Id header
    // (same mechanism as the org switcher).
    setOrg(event.org_id, event.org_name);
    queryClient.removeQueries();
    navigate(`/waste/events/${event.event_id}`);
  }

  const data = comparisonQuery.data;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-medium">Monitoring</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Waste-type scorer v1-vs-v2 shadow comparison. Shadow values are visible here only —
            never in customer-facing surfaces until cutover.
          </p>
        </div>
        <BackfillDialog />
      </div>

      {comparisonQuery.isLoading && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      )}

      {comparisonQuery.isError && (
        <div className="rounded border border-destructive/50 bg-destructive/10 p-3">
          <p className="text-sm font-medium text-destructive">Failed to load comparison</p>
          <p className="mt-1 font-mono text-xs text-destructive/80">
            {comparisonQuery.error.message}
          </p>
        </div>
      )}

      {data && data.totals.scored_rows === 0 && (
        <div className="rounded-lg border bg-card p-8 text-center">
          <p className="text-sm font-medium">No shadow pairs yet</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            The scorer writes a shadow value on every new AI classification. Pairs accumulate from
            the first classification after the POS2-896 deploy — check back after device activity.
          </p>
          {data.totals.consensus_rows > 0 && (
            <p className="mt-2 text-xs text-muted-foreground">
              {(data.totals.consensus_rows - data.totals.scored_rows).toLocaleString()} pre-deploy
              classifications carry no shadow value.
            </p>
          )}
        </div>
      )}

      {data && data.totals.scored_rows > 0 && (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            <StatCard
              label="Shadow pairs"
              value={data.totals.pairs.toLocaleString()}
              hint={
                data.confirmed.pairs > 0
                  ? `${data.confirmed.pairs.toLocaleString()} confirmed · ${pct(
                      data.confirmed.agreement_pct,
                    )} match human`
                  : "unconfirmed v1-vs-v2"
              }
            />
            <StatCard
              label="Agreement"
              value={pct(data.totals.agreement_pct)}
              hint="v1 = v2 (unconfirmed)"
            />
            <StatCard
              label="Unassigned rate"
              value={`${pct(data.totals.v1_unassigned_pct)} → ${pct(data.totals.v2_unassigned_pct)}`}
              hint={`${data.totals.unassigned_delta_pct <= 0 ? "" : "+"}${pct(
                data.totals.unassigned_delta_pct,
              )} v1→v2`}
            />
            <StatCard
              label="Evidence coverage"
              value={pct(data.totals.evidence_coverage_pct)}
              hint={`${data.totals.with_state_evidence.toLocaleString()} of ${data.totals.scored_rows.toLocaleString()} scored`}
            />
          </div>

          <Crosstab crosstab={data.crosstab} selected={cell} onSelect={selectCell} />

          <PerSiteTable rows={data.per_site} />

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <DistributionCard title="Scorer confidence" rows={data.confidence_distribution} />
            <DistributionCard title="Unassigned reasons" rows={data.reason_distribution} />
          </div>

          <Disagreements
            query={disagreementsQuery}
            sites={data.per_site}
            cell={cell}
            siteFilter={siteFilter}
            confirmedFilter={confirmedFilter}
            page={page}
            pageSize={pageSize}
            onSite={(v) => {
              setSiteFilter(v);
              resetPaging();
            }}
            onConfirmed={(v) => {
              setConfirmedFilter(v);
              resetPaging();
            }}
            onClearCell={() => {
              setCell(null);
              resetPaging();
            }}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(0);
            }}
            onOpen={openEvent}
          />
        </>
      )}
    </div>
  );
}

function Crosstab({
  crosstab,
  selected,
  onSelect,
}: {
  crosstab: CrosstabCell[];
  selected: { v1: string; v2: string } | null;
  onSelect: (v1: string, v2: string) => void;
}) {
  const lookup = new Map(crosstab.map((c) => [`${c.v1}|${c.v2}`, c.n]));
  const rowTypes = WASTE_TYPES.filter((t) => crosstab.some((c) => c.v1 === t));
  const colTypes = WASTE_TYPES.filter((t) => crosstab.some((c) => c.v2 === t));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">
          v1 → v2 crosstab
          <span className="ml-2 font-normal text-muted-foreground">
            rows = v1 (live), columns = v2 (scorer); diagonal = agreement. Click an off-diagonal
            cell to drill in.
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="p-2 text-left font-medium text-muted-foreground">v1 ＼ v2</th>
              {colTypes.map((c) => (
                <th key={c} className="p-2 text-right font-medium">
                  {wasteTypeLabelEnglish(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowTypes.map((r) => (
              <tr key={r} className="border-t">
                <td className="p-2 font-medium">{wasteTypeLabelEnglish(r)}</td>
                {colTypes.map((c) => {
                  const n = lookup.get(`${r}|${c}`) ?? 0;
                  const isDiagonal = r === c;
                  const isSelected = selected?.v1 === r && selected?.v2 === c;
                  return (
                    <td key={c} className="p-1 text-right">
                      <button
                        type="button"
                        // Diagonal cells are agreement (v1 == v2); the disagreements
                        // RPC excludes them by construction, so drilling in would
                        // always be empty — non-clickable.
                        disabled={n === 0 || isDiagonal}
                        onClick={() => onSelect(r, c)}
                        className={[
                          "w-full rounded px-2 py-1 text-right tabular-nums",
                          n === 0 ? "text-muted-foreground/40" : "",
                          isDiagonal && n > 0 ? "bg-emerald-50 text-emerald-700" : "",
                          !isDiagonal && n > 0 ? "hover:bg-accent" : "",
                          isSelected ? "ring-2 ring-primary" : "",
                        ].join(" ")}
                      >
                        {n}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function PerSiteTable({ rows }: { rows: SiteComparison[] }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Per-site</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Site</TableHead>
              <TableHead className="text-right">Pairs</TableHead>
              <TableHead className="text-right">Agreement</TableHead>
              <TableHead className="text-right">v1 unassigned</TableHead>
              <TableHead className="text-right">v2 unassigned</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((s) => (
              <TableRow key={s.site_id}>
                <TableCell className="font-medium">{s.site_name}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {s.pairs.toLocaleString()}
                </TableCell>
                <TableCell className="text-right tabular-nums">{pct(s.agreement_pct)}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {pct(s.v1_unassigned_pct)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {pct(s.v2_unassigned_pct)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function DistributionCard({ title, rows }: { title: string; rows: DistributionEntry[] }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">None</p>
        ) : (
          <div className="space-y-1">
            {rows.map((r) => (
              <div key={r.key} className="flex items-center justify-between text-sm">
                <span>{r.key}</span>
                <span className="tabular-nums text-muted-foreground">{r.n.toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Disagreements({
  query,
  sites,
  cell,
  siteFilter,
  confirmedFilter,
  page,
  pageSize,
  onSite,
  onConfirmed,
  onClearCell,
  onPageChange,
  onPageSizeChange,
  onOpen,
}: {
  query: ReturnType<typeof useQuery<{ events: DisagreementEvent[]; total: number }, Error>>;
  sites: SiteComparison[];
  cell: { v1: string; v2: string } | null;
  siteFilter: string;
  confirmedFilter: string;
  page: number;
  pageSize: number;
  onSite: (v: string) => void;
  onConfirmed: (v: string) => void;
  onClearCell: () => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onOpen: (event: DisagreementEvent) => void;
}) {
  const data = query.data;
  const events = data?.events ?? [];
  const total = data?.total ?? 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Disagreements</CardTitle>
        <div className="flex flex-wrap items-center gap-2 pt-2">
          {cell && (
            <Badge variant="outline" className="gap-1">
              {wasteTypeLabelEnglish(cell.v1)} → {wasteTypeLabelEnglish(cell.v2)}
              <button type="button" onClick={onClearCell} className="ml-1 hover:text-foreground">
                ×
              </button>
            </Badge>
          )}
          <Select value={siteFilter} onValueChange={onSite}>
            <SelectTrigger className="h-8 w-48">
              <SelectValue placeholder="All sites" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sites</SelectItem>
              {sites.map((s) => (
                <SelectItem key={s.site_id} value={s.site_id}>
                  {s.site_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={confirmedFilter} onValueChange={onConfirmed}>
            <SelectTrigger className="h-8 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="true">Confirmed</SelectItem>
              <SelectItem value="false">Unconfirmed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {query.isLoading && <Skeleton className="h-40 w-full" />}
        {query.isError && (
          <p className="font-mono text-xs text-destructive">{query.error.message}</p>
        )}
        {data && events.length === 0 && total === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">No disagreements.</p>
        )}
        {events.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Org / Site</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead>v1 → v2</TableHead>
                <TableHead>Signals</TableHead>
                <TableHead>Confirmed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((e) => (
                <TableRow key={e.event_id} className="cursor-pointer" onClick={() => onOpen(e)}>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    {new Date(e.logged_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="font-medium">{e.site_name}</div>
                    <div className="text-muted-foreground">{e.org_name}</div>
                  </TableCell>
                  <TableCell className="text-xs">L{e.tier}</TableCell>
                  <TableCell className="text-xs">
                    <span className="text-muted-foreground">{wasteTypeLabelEnglish(e.v1)}</span> →{" "}
                    <span className="font-medium">{wasteTypeLabelEnglish(e.v2)}</span>
                    {e.scorer_confidence && (
                      <span className="ml-1 text-muted-foreground">({e.scorer_confidence})</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {e.scorer_signals && e.scorer_signals.length > 0
                      ? e.scorer_signals.join(", ")
                      : (e.scorer_reason ?? "—")}
                  </TableCell>
                  <TableCell>
                    {e.is_confirmed && <Badge variant="secondary">confirmed</Badge>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <PaginationBar
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pageSizeOptions={[25, 50, 100]}
        />
      </CardContent>
    </Card>
  );
}

function LabelledSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
}) {
  return (
    <div className="space-y-1">
      <span className="block text-xs text-muted-foreground">{label}</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8 w-52">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map(([v, l]) => (
            <SelectItem key={v} value={v}>
              {l}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// POS2-899 — temporary admin tool to re-score the historical Unassigned/NULL backlog.
// Dry-run always writes nothing; a real run is refused server-side until SCORER_V2_LIVE
// is on. Triggered runs execute in the background, so the runs list polls while any run
// is still running.
function BackfillDialog() {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const authed = !!headers.Authorization.replace("Bearer ", "");

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("rescue");
  const [tier, setTier] = useState("1");
  const [runType, setRunType] = useState("dry");
  const [message, setMessage] = useState<string | null>(null);

  const runsQuery = useQuery<BackfillRun[], Error>({
    queryKey: ["admin", "monitoring", "backfill-runs"],
    queryFn: () =>
      fetchJson<BackfillRun[]>(
        `${API_URL}/api/v1/admin/monitoring/waste-type-backfill/runs`,
        headers,
      ),
    // Only fetch (and poll) while the dialog is open.
    enabled: authed && open,
    retry: false,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((r) => r.status === "running") ? 3000 : false,
  });

  const trigger = useMutation<BackfillTriggerResponse, Error, void>({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/admin/monitoring/waste-type-backfill`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ mode, tier, dry_run: runType === "dry" }),
      });
      if (!res.ok) {
        throw new Error(`${res.status}: ${await res.text().catch(() => res.statusText)}`);
      }
      return ((await res.json()) as { data: BackfillTriggerResponse }).data;
    },
    onSuccess: (data) => {
      setMessage(data.detail);
      void queryClient.invalidateQueries({ queryKey: ["admin", "monitoring", "backfill-runs"] });
    },
    onError: (err) => setMessage(err.message),
  });

  const runs = runsQuery.data ?? [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          Waste-type backfill
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] gap-4 overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Waste-type backfill</DialogTitle>
          <DialogDescription>
            Re-score the historical Unassigned backlog. Dry-run writes nothing; a real run is
            refused until the scorer v2 cutover (SCORER_V2_LIVE).
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-3">
          <LabelledSelect
            label="Mode"
            value={mode}
            onChange={setMode}
            options={[
              ["rescue", "Rescue (unassigned only)"],
              ["rederive", "Re-derive (all unconfirmed)"],
            ]}
          />
          <LabelledSelect
            label="Tier"
            value={tier}
            onChange={setTier}
            options={[
              ["1", "1 — evidence (high/medium)"],
              ["2", "2 — weight prior (low)"],
              ["all", "All (evidence + weight)"],
            ]}
          />
          <LabelledSelect
            label="Run"
            value={runType}
            onChange={setRunType}
            options={[
              ["dry", "Dry-run (no writes)"],
              ["real", "Execute"],
            ]}
          />
          <Button
            type="button"
            disabled={trigger.isPending}
            onClick={() => {
              setMessage(null);
              trigger.mutate();
            }}
          >
            {trigger.isPending ? "Scheduling…" : "Run backfill"}
          </Button>
        </div>

        {message && <p className="text-xs text-muted-foreground">{message}</p>}

        {runs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No runs yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Started</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Scorer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Scanned</TableHead>
                  <TableHead className="text-right">Assigned</TableHead>
                  <TableHead className="text-right">Unassigned</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {new Date(r.started_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-xs">{r.mode}</TableCell>
                    <TableCell className="text-xs">{r.tier}</TableCell>
                    <TableCell className="text-xs">{r.dry_run ? "dry-run" : "execute"}</TableCell>
                    <TableCell className="text-xs">{r.scorer_live}</TableCell>
                    <TableCell>
                      <Badge variant={r.status === "completed" ? "secondary" : "outline"}>
                        {r.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {(r.counts.scanned ?? 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {(
                        sumCounts(r.counts.assigned_by_type) + sumCounts(r.counts.refined_by_type)
                      ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {r.counts.unassigned_before ?? 0} → {r.counts.unassigned_after ?? 0}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** English wording for a waste type. This is a staff-only, English-only surface (pinned decision
 * 4), so it reads the English record directly rather than the reader's catalogue (POS2-1134). */
function wasteTypeLabelEnglish(key: string): string {
  return WASTE_TYPE_LABELS[key] ?? key;
}
